import React, { useRef, useState } from "react";
import { observer, inject } from "mobx-react";
import { useTranslation } from "react-i18next";
import camera from "../asserts/img/camera.svg";
import styled, { useTheme } from "styled-components";
import { ClipLoader } from "react-spinners";
import { useMobile } from "../utils/mobile";
import { isAcceptedFormat } from "../utils/helper";
import { MAX_FILE_SIZE_MB } from "../utils/formHelpers";
import { Text } from "../UI";

const FileInput = styled("input")`
  display: none;
`;

const FileError = styled("div")(({ theme }) => ({
  color: theme.color.error,
  fontSize: 12,
  textAlign: "start",
}));

// `accept`: optional list (or comma-separated string) of accepted MIME types, also used
// to filter the file picker. `onAdd(event, files)` gets the change event and the valid files
// (`multiple` lets the user pick several at once; invalid ones are reported and skipped).
const ExtentionInsert = ({
  extentions,
  onAdd,
  accept,
  singleFile = false,
  isWeb = false,
  multiple = false,
}) => {
  const { t } = useTranslation("support_calls");

  const theme = useTheme();
  const inputRef = useRef(null);
  const isMobile = useMobile();
  const [isProcessing, setProcessing] = useState(false);
  const [fileError, setFileError] = useState("");
  const disabled = (singleFile && extentions?.length === 1) || isProcessing;
  const acceptList = Array.isArray(accept) ? accept : accept?.split(",");

  const onImgClick = () => {
    inputRef.current.click();
  };

  const validateFile = (file) => {
    if (acceptList?.length && !isAcceptedFormat(file, acceptList)) {
      return t("error:format_file_type_not_allowed");
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return t("error:file_too_large", { size: MAX_FILE_SIZE_MB });
    }
    return "";
  };

  const handleChange = async (e) => {
    const pickedFiles = Array.from(e.target.files || []);
    // Picker was cancelled - nothing to add
    if (!pickedFiles.length || disabled) return;

    const validationErrors = pickedFiles.map(validateFile);
    const validFiles = pickedFiles.filter((file, index) => !validationErrors[index]);
    setFileError(validationErrors.find(Boolean) || "");

    if (validFiles.length) {
      setProcessing(true);
      try {
        // Parents read the files (base64 encoding) here - show progress until it's done
        await onAdd(e, validFiles);
      } catch (err) {
        console.error("Failed to add files", validFiles.map((file) => file.name), err);
        setFileError(t("error:error_failed_upload_file"));
      } finally {
        setProcessing(false);
      }
    }

    // Reset so removing a file and picking the same one again fires onChange
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <>
      <div
        style={{
          margin: "10px 0",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 10,
          alignItems: isMobile ? "flex-start" : "flex-start",
          flexWrap: "nowrap",
        }}
      >
        <Text.H3 color={theme.color.text}>{t("add_files")}</Text.H3>
        <FileInput
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={acceptList?.join(",")}
          onChange={handleChange}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <button
            type="button"
            onClick={onImgClick}
            disabled={disabled}
            aria-busy={isProcessing}
            style={{
              border: `1.4px solid ${theme.color.mainBlue}`,
              backgroundColor: theme.color.white,
              color: theme.color.mainBlue,
              borderRadius: 20,
              padding: 5,
              fontSize: 14,
              fontWeight: 500,
              fontFamily: "inherit",
              gap: 14,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              height: 36,
              lineHeight: "26px",
              cursor: disabled ? "default" : "pointer",
              opacity: disabled && !isProcessing ? 0.5 : 1,
            }}
          >
            {isProcessing ? (
              <span style={{ marginInlineStart: "8px", display: "flex" }}>
                <ClipLoader color={theme.color.mainBlue} size={18} />
              </span>
            ) : (
              <img src={camera} alt="" style={{ marginInlineStart: "8px" }} />
            )}

            <span
              style={{
                marginInlineEnd: "8px",
                overflow: "hidden",
                whiteSpace: "wrap",
                textOverflow: "ellipsis",
                textAlign: "start",
                lineHeight: "24px",
              }}
            >
              {isWeb ? t("add_file") : t("add_files_or_images")}
            </span>
          </button>
          {!!fileError && <FileError role="alert">{fileError}</FileError>}
        </div>
      </div>
    </>
  );
};

export default inject()(observer(ExtentionInsert));
